import { StatusCodes } from 'http-status-codes';

import { errorCodeTypes as floorErrorCodeTypes } from '../floors';
import { errorCodeTypes as unitErrorCodeTypes } from '../units';
import { errorCodeTypes as tenantErrorCodeTypes } from '../tenants';
import { errorCodeTypes as workflowErrorCodeTypes } from '../workflows';
import { errorCodeTypes as workOrdersErrorCodeTypes } from '../work-orders';
import { errorCodeTypes as phoneVerificationErrorCodeTypes } from '../phoneVerification';

export type ErrorCodeType = {
  // a unique code used to determine what the error is, this will be included
  // in the error message so that errors can be determined after being
  // serialized, transformed, displayed, etc.
  code: string;
  // the name of the error object
  name: string;
  // the message for this error
  message: string;
  // HTTP status code if applicable
  status?: number;

  errorType?: string;

  safetrustCode?: string;
};

export const errorCodes = {
  alreadyExistsError: {
    code: 'AE01',
    name: 'AlreadyExistsError',
    message: 'This already exists.',
  },
  versionConflictError: {
    code: 'VC01',
    name: 'VersionConflictError',
    message:
      'This was changed by someone else. Reload and apply your edit again.',
    status: StatusCodes.CONFLICT,
  },
  audienceNotFoundError: {
    code: 'AUD01',
    name: 'AudienceNotFoundError',
    message: 'This Audience could not be found.',
    status: StatusCodes.NOT_FOUND,
  },
  audienceMissingFieldError: {
    code: 'AUD02',
    name: 'AudienceMissingFieldError',
    message: 'This Audience is missing a required field.',
    status: StatusCodes.BAD_REQUEST,
  },
  audienceAttachContentError: {
    code: 'AUD03',
    name: 'AudienceAttachContentError',
    message:
      'This piece of content cannot be associated to one or more of the audiences specified',
    status: StatusCodes.BAD_REQUEST,
  },
  audienceNotEmptyError: {
    code: 'AUD04',
    name: 'AudienceNotEmptyError',
    message:
      'This audience could not be deleted because there is content attached to it',
    status: StatusCodes.CONFLICT,
  },
  badRequestError: {
    code: 'R01',
    name: 'BadRequestError',
    message: 'Bad Request.',
    status: StatusCodes.BAD_REQUEST,
  },
  channelCreationError: {
    code: 'CH01',
    name: 'ChannelCreationError',
    message: 'Cannot create this channel',
  },
  channelFiltersError: {
    code: 'CH02',
    name: 'ChannelFiltersError',
    message: 'You do not have the required email address to join this channel.',
  },
  channelIntegrationNotSetupError: {
    code: 'CI01',
    name: 'ChannelIntegrationNotSetupError',
    message: 'This channel integration is not properly setup.',
  },
  channelInviteOnlyError: {
    code: 'CH03',
    name: 'ChannelInviteOnlyError',
    message: 'This Channel is Invite Only.',
  },
  channelMaxUsersError: {
    code: 'CH04',
    name: 'ChannelMaxUsersError',
    message: 'This channel is at max users',
  },
  channelNotSetupError: {
    code: 'CH06',
    name: 'ChannelNotSetupError',
    message: 'A Channel must have an address set before it can create content.',
  },
  channelPrivateError: {
    code: 'CH07',
    name: 'ChannelPrivateError',
    message: 'This Channel is Private.',
  },
  removeUserFromChannelError: {
    code: 'CH08',
    name: 'removeUserFromChannelError',
    message: 'Unable to remove user from channel.',
  },
  channelRelationshipCreationError: {
    code: 'CR01',
    name: 'ChannelRelationshipCreationError',
    message: 'Creating this Channel Relationship failed.',
  },
  completeSignupExpiredError: {
    code: 'SU01',
    name: 'CompleteSignupExpiredError',
    message: 'Your signup link has expired.',
  },
  contentNotEnoughQuantity: {
    code: 'CF01',
    name: 'ContentNotEnoughQuantityError',
    message: 'Unfortunately, these have all been claimed',
  },
  contentPublishError: {
    code: 'CP01',
    name: 'ContentPublishError',
    message: 'A piece of content must be targeted before being published.',
  },
  eventContentMissingStartOrEndDateError: {
    code: 'CP02',
    name: 'EventContentMissingStartOrEndDateError',
    message: 'Content marked as an event must have event dates.',
  },
  eventContentInvalidDateRangeError: {
    code: 'CP03',
    name: 'EventContentInvalidDateRangeError',
    message: 'Start date must be before end date.',
  },
  contentMissingTitleError: {
    code: 'CT01',
    name: 'ContentMissingTitleError',
    message: 'Draft Content must have a title',
  },
  contentDoesNotExistError: {
    code: 'CT02',
    name: 'ContentDoesNotExistError',
    message: 'This piece of content does not exist',
    status: StatusCodes.NOT_FOUND,
  },
  contentMissingCompleteWorkflowError: {
    code: 'CT03',
    name: 'ContentMissingCompleteWorkflowError',
    message: 'Complete all workflows before publishing.',
  },
  contentRequestedMoreThanMax: {
    code: 'CF02',
    name: 'ContentRequestedMoreThanMaxError',
    message: 'You requested more than the maximum allowed amount per person',
  },
  notificationValidationError: {
    code: 'NT01',
    name: 'NotificationValidationError',
    message: 'Please ensure that the notification meets the required criteria.',
  },
  notLoggedIn: {
    code: 'L01',
    name: 'NotLoggedInError',
    message: 'You are not logged in.',
    status: StatusCodes.UNAUTHORIZED,
  },
  notFoundError: {
    code: 'NF01',
    name: 'NotFoundError',
    message: 'This resource was not found.',
    status: StatusCodes.NOT_FOUND,
  },
  notImplementedError: {
    code: 'NI01',
    name: 'NotImplementedError',
    message: 'This is not yet implemented.',
    status: StatusCodes.NOT_IMPLEMENTED,
  },
  networkRequestFailedError: {
    code: 'NW01',
    name: 'NetworkRequestFailedError',
    message: 'Network request failed.',
  },
  passwordResetExpiredError: {
    code: 'PW01',
    name: 'PasswordResetExpiredError',
    message: 'Your password reset link has expired, please try again.',
    status: StatusCodes.UNAUTHORIZED,
  },
  oauthNotSetupError: {
    code: 'PC04',
    name: 'OAuthNotSetupError',
    message: 'You need to link this OAuth account',
  },
  loginInvalidError: {
    code: 'L02',
    name: 'LoginInvalidError',
    message: 'You’ve entered an invalid email or password.',
  },
  loginNotExistsError: {
    code: 'L03',
    name: 'LoginNotExistsError',
    message: 'This login does not yet exist.',
    status: StatusCodes.NOT_FOUND,
  },
  magicLinkExpiredError: {
    code: 'L04',
    name: 'MagicLinkExpiredError',
    message: 'This magic link has expired.',
    status: StatusCodes.BAD_REQUEST,
  },
  passwordIsEmptyError: {
    code: 'L05',
    name: 'passwordIsEmptyError',
    message: 'Password is empty.',
    status: StatusCodes.UNPROCESSABLE_ENTITY,
  },
  emailAlreadyInUseError: {
    code: 'E02',
    name: 'EmailAlreadyInUseError',
    message: 'This email address is already in use.',
  },
  phoneNumberAlreadyInUseError: {
    code: 'E05',
    name: 'PhoneNumberAlreadyInUseError',
    message: 'This phone number is already in use.',
  },
  unverifiedEmailError: {
    code: 'E03',
    name: 'UnverifiedEmailError',
    message: 'You must verify your email to login.',
  },
  invalidEmailError: {
    code: 'E04',
    name: 'InvalidEmailError',
    message: 'This email has bounced or has registered a complaint.',
  },
  unverifiedEmailPersistsError: {
    code: 'E06',
    name: 'UnverifiedEmailErrorChannel',
    message: 'Email has not been verified yet.',
  },
  unverifiedEmailNotFound: {
    code: 'E07',
    name: 'unverifiedEmailNotFound',
    message: 'This email is not found or verified already.',
  },
  invalidMediaContentTypeError: {
    code: 'ME01',
    name: 'InvalidMediaContentTypeError',
    message: 'This media type isn’t supported.',
  },
  inviteClaimedError: {
    code: 'IV01',
    name: 'InviteClaimedError',
    message: 'This invite has already been claimed.',
  },
  inviteExpiredError: {
    code: 'IV02',
    name: 'InviteExpiredError',
    message: 'This invite has expired.',
  },
  inviteInvalidEmailError: {
    code: 'IV03',
    name: 'InviteInvalidEmailError',
    message: 'This invite email did not match the email you signed up with.',
  },
  missingParametersError: {
    code: 'MP01',
    name: 'MissingParametersError',
    message: 'You are missing parameters.',
  },
  paymentNotSetupError: {
    code: 'P01',
    name: 'PaymentNotSetupError',
    message: 'You have not setup a valid payment option for your account.',
  },
  paymentFailedError: {
    code: 'P02',
    name: 'PaymentFailedError',
    message: 'This payment failed, see details for more info.',
  },
  invalidCardError: {
    code: 'P03',
    name: 'InvalidCardError',
    message:
      'Your card could not be validated. Please check your information and try again.',
  },
  paymentSettingsAccessPermission: {
    code: 'P04',
    name: 'PaymentSettingsAccessPermission',
    message:
      'You do not currently have permission to access the payment settings for this user.',
  },
  paymentTimeoutError: {
    code: 'P05',
    name: 'PaymentTimeoutError',
    message: 'The payment request has timed out.',
  },
  paymentRequestInvalidError: {
    code: 'P06',
    name: 'PaymentRequestInvalidError',
    message: 'This payment request is invalid.',
  },
  transactionFailedError: {
    code: 'P07',
    name: 'PaymentTransactionFailedError',
    message: 'Unable to complete payment.',
  },
  inUseError: {
    code: 'I01',
    name: 'InUseError',
    message: "You can't remove this because it is in use.",
  },
  invalidStatusTransitionError: {
    code: 'FS01',
    name: 'InvalidStatusTransitionError',
    message:
      'Invalid status transition. Please contact the creator of this content for assistance',
  },
  invalidIdError: {
    code: 'ID01',
    name: 'InvalidIdError',
    message: 'This id is invalid.',
  },
  invalidFeatureStateError: {
    code: 'FS03',
    name: 'InvalidFeatureStateError',
    message: 'Invalid Feature State',
  },
  invalidTypeValueError: {
    code: 'TV03',
    name: 'InvalidTypeValueError',
    message: 'Invalid type value',
  },
  missingStatusTransitionRuleError: {
    code: 'FS02',
    name: 'MissingStatusTransitionRuleError',
    message: 'There is no rule from this status to the next.',
  },
  contentFeatureDoesntExistError: {
    code: 'CF01',
    name: 'ContentFeatureDoesntExistError',
    message: 'This feature does not exist on this Content.',
  },
  contentFeatureCancelableError: {
    code: 'CFC01',
    name: 'ContentFeatureCancelableError',
    message: 'This interaction is over and cannot be Cancelled',
  },
  dataSecurityError: {
    code: 'DS01',
    name: 'DataSecurityError',
    message: 'This data cannot be read.',
  },
  contentFeatureReservableNotAvailableError: {
    code: 'CFR01',
    name: 'ContentFeatureReservableNotAvailableError',
    message: 'This time is no longer available',
  },
  contentFeatureGuestInviteError: {
    code: 'CFG01',
    name: 'ContentFeatureGuestInviteError',
    message: 'Invalid number of guests invited.',
  },
  groupRoleExistsError: {
    code: 'GR01',
    name: 'GroupRoleExistsError',
    message: 'A GroupRole with this name already exists',
  },
  groupRoleInUseError: {
    code: 'GR02',
    name: 'GroupRoleInUseError',
    message: 'This GroupRole is still in use',
  },
  groupRoleRequirementsError: {
    code: 'GR03',
    name: 'GroupRoleRequirementsError',
    message: "Can't add GroupRole requirements while it has existing users",
  },
  moduleGroupRoleUpdateError: {
    code: 'GR04',
    name: 'ModuleGroupRoleUpdateError',
    message: 'Module group roles cannot be updated.',
    status: StatusCodes.FORBIDDEN,
  },
  moduleGroupRoleDeleteRestrictedError: {
    code: 'GR05',
    name: 'ModuleGroupRoleDeleteRestrictedError',
    message: 'Module group roles cannot be deleted.',
    status: StatusCodes.FORBIDDEN,
  },
  groupRoleTemplateNameCollision: {
    code: 'GRT01',
    name: 'GroupRoleTemplateNameCollisionError',
    message:
      'The team name provided is reserved. Please use a different team name.',
  },
  userGroupRoleMaxUsersError: {
    code: 'UGM01',
    name: 'UserGroupRoleMaxUsersError',
    message: 'This group is at max users.',
  },
  notAuthorizedError: {
    code: 'AUTH01',
    name: 'NotAuthorizedError',
    message: 'You are not authorized to perform this action.',
    status: StatusCodes.UNAUTHORIZED,
  },
  uciExpiredError: {
    code: 'UCI01',
    name: 'UCIExpiredError',
    message: 'Has ended.',
  },
  invalidInteractionStatusForQRScan: {
    code: 'UCI02',
    name: 'InvalidInteractionStatusForQRScan',
    message: 'This interaction is not in a valid state',
  },
  contentStartedError: {
    code: 'CS01',
    name: 'ContentStartedError',
    message: 'Has not started yet.',
  },
  contentFeatureUpdateError: {
    code: 'CFU01',
    name: 'ContentFeatureUpdateError',
    message: 'This booking can no longer be updated.',
    status: StatusCodes.FORBIDDEN,
  },
  secureIdRequiredError: {
    code: 'SI01',
    name: 'SecureIdRequiredError',
    message: 'You need a Secure Id to complete this.',
  },
  secureIdExpiredError: {
    code: 'SI02',
    name: 'SecureIdExpiredError',
    message: 'This Secure ID has expired.',
  },
  secureIdInvalidError: {
    code: 'SI03',
    name: 'SecureIdInvalidError',
    message: 'This Secure ID is invalid.',
  },
  slugExistsError: {
    code: 'SL01',
    name: 'SlugExistsError',
    message: 'Slug already exists.',
  },
  tabIconExistsError: {
    code: 'TAB01',
    name: 'TabIconExistsError',
    message: 'A tab with this icon already exists.',
  },
  invalidQRCodeError: {
    code: 'QR01',
    name: 'InvalidQRCodeError',
    message: 'This QR Code is invalid.',
  },
  userGroupRoleExistsError: {
    code: 'GR01',
    name: 'UserGroupRoleExistsError',
    message: 'A UserGroupRole for this user already exists.',
  },
  angusIntegrationBadSetup: {
    code: 'IA02',
    name: 'AngusIntegrationBadSetupError',
    errorType: 'invalid_client',
    message: 'Please contact your property manager or try again later.',
  },
  homIntegrationSetupError: {
    code: 'H01',
    name: 'HomIntegrationSetupError',
    message: 'HOM integration is not setup properly.',
  },
  homRSVPError: {
    code: 'H02',
    name: 'HomRSVPError',
    message: 'Was not able to RSVP to HOM Event.',
  },
  homUNRSVPError: {
    code: 'H02',
    name: 'HomUNRSVPError',
    message: 'Was not able to UNRSVP to HOM Event.',
  },
  newstandIntegrationSetupError: {
    code: 'NS01',
    name: 'NewstandIntegrationSetupError',
    message: 'Newstand integration is not setup properly.',
  },
  newstandDigestError: {
    code: 'NS02',
    name: 'NewstandDigestError',
    message: 'Error processing Newstand Digest.',
  },
  parkWhizOAuthNotSetupError: {
    code: 'PW01',
    name: 'ParkWhizOAuthNotSetupError',
    message: 'ParkWhiz account is not setup.',
  },
  angusApiError: {
    code: 'IA03',
    name: 'AngusApiError',
    message: 'Please contact your property manager or try again later.',
  },
  placePayError: {
    code: 'PLP01',
    name: 'PlacePayError',
    message: 'Encountered an error with PlacePay, please contact support.',
  },
  proxyClickApiError: {
    code: 'PC01',
    name: 'ProxyClickApiError',
    message: 'Please contact your property manager or try again later.',
  },
  essensysIntegrationSetupError: {
    code: 'EI01',
    name: 'EssensysIntegrationSetupError',
    message:
      'Essensys integration is not setup properly! Please contact your account manager or try again later.',
  },
  essensysApiError: {
    code: 'EI02',
    name: 'EssensysApiError',
    message:
      'Essensys API error. Please contact your account manager or try again later.',
  },
  essensysRequestContainsValidationError: {
    code: 'EI03',
    name: 'EssensysRequestContainsValidationError',
    message:
      'Essensys Request error. Please contact your account manager or try again later.',
  },
  essensysSyncError: {
    code: 'ES01',
    name: 'EssensysSyncError',
    message: 'A failure has occurred during the sync process',
  },
  essensysBookingConflictError: {
    code: 'EI04',
    name: 'EssensysBookingConflictError',
    message:
      'This timeslot is already reserved, select another timeslot and try again.',
  },
  essensysSubmitOnBehalfOfError: {
    code: 'EI05',
    name: 'EssensysSubmitOnBehalfOfError',
    message:
      'Bookings can only be made on behalf of Essensys users that are on account.',
  },
  oAuthConfigError: {
    code: 'OA01',
    name: 'OAuthConfigError',
    message: 'Please contact your property manager or try again later',
    status: StatusCodes.UNAUTHORIZED,
  },
  oAuthIdentityExpiredError: {
    code: 'OA02',
    name: 'OAuthIdentityExpiredError',
    message: 'An expired id_token has been rejected.',
    status: StatusCodes.UNAUTHORIZED,
  },
  oAuthClientSecretError: {
    code: 'OA03',
    name: 'oAuthClientSecretError',
    message: 'A client_secret will be required for the given provider.',
    status: StatusCodes.UNAUTHORIZED,
  },
  singleSignOnProviderNotAvailable: {
    code: 'SSO01',
    name: 'SingleSignOnProviderNotAvailableError',
    message: 'The SSO provider you are attempting to use is not supported.',
  },
  singleSignOnOpenIdNotAvailable: {
    code: 'SSO02',
    name: 'SingleSignOnOpenIdNotAvailableError',
    message:
      'Auto-discovery failed for the SSO provider you are using, please try again later.',
  },
  singleSignOnIdTokenRejected: {
    code: 'SSO03',
    name: 'SingleSignOnIdTokenRejectedError',
    message: 'The provided `id_token` has been rejected',
  },
  signUpPrivateLocationError: {
    code: 'SU02',
    name: 'SignUpPrivateLocationError',
    message: 'Sorry, you must request access to join this location.',
  },
  signUpFullAccessError: {
    code: 'SU03',
    name: 'SignUpFullAccessError',
    message:
      'Some content may not be available, you can contact your property manager for full access.',
  },
  ApiKeyEnvironmentError: {
    code: 'AKE01',
    name: 'ApiKeyEnvironmentError',
    message:
      'key is not supported in this environment. Make sure you use the correct key.',
    status: StatusCodes.UNAUTHORIZED,
  },
  signUpUnsupportedLocationError: {
    code: 'SU04',
    name: 'SignUpUnsupportedLocationError',
    message: 'Sorry, this location is not supported yet.',
  },
  outlookCalendarChannelIntegrationSettingsNotFound: {
    code: 'OCI01',
    name: 'OutlookCalendarChannelIntegrationSettingsNotFound',
    message: 'Outlook Calendar channel integration is not setup!',
  },
  outlookCalendarInvalidFeatureSetup: {
    code: 'OCI02',
    name: 'OutlookCalendarInvalidFeatureSetup',
    message:
      'Outlook Calendar feature is not setup properly! Please contact your account manager or try again later.',
  },
  outlookCalendarReservableFeatureNotFound: {
    code: 'OCI03',
    name: 'OutlookCalendarReservableFeatureNotFound',
    message:
      'Outlook Calendar feature is not setup properly! Please contact your account manager or try again later.',
  },
  outlookCalendarTimeNotAvailableError: {
    code: 'OCI04',
    name: 'OutlookCalendarTimeNotAvailableError',
    message:
      'This time is no longer available. Please select another timeslot and try again.',
  },
  outlookCalendarEventIdMissingError: {
    code: 'OCI05',
    name: 'OutlookCalendarEventIdMissingError',
    message:
      'Failed to create Outlook Calendar event. Please try again later or contact support if the issue persists.',
  },
  outlookCalendarSubscriptionError: {
    code: 'OCI06',
    name: 'OutlookCalendarSubscriptionError',
    message:
      'Failed to create Outlook Calendar subscription. Please try again later or contact support if the issue persists.',
  },
  shopifyMultipassIntegrationSetupError: {
    code: 'SMI01',
    name: 'shopifyMultipassIntegrationSetupError',
    message: 'ShopifyMultipass integration is not setup properly.',
  },
  shopifyMultipassNoVerifiedEmailError: {
    code: 'SMI02',
    name: 'shopifyMultipassNoVerifiedEmailError',
    message:
      'Unable to find valid verified email, please verify the email you wish to use with this store.',
  },
  shopifyMultipassStoreRequestError: {
    code: 'SMI03',
    name: 'shopifyMultipassStoreRequestError',
    message: 'Failed to query Shopify store API.',
  },
  shopifyMultipassUserNotFound: {
    code: 'SMI04',
    name: 'shopifyMultipassUserNotFound',
    message:
      'We can’t seem to find that email. Please double check your email and try again.',
  },
  googleCalendarChannelIntegrationSettingsNotFound: {
    code: 'GCI01',
    name: 'GoogleCalendarChannelIntegrationSettingsNotFound',
    message: 'Google Calendar channel integration is not setup!',
  },
  googleCalendarInvalidFeatureSetup: {
    code: 'GCI02',
    name: 'GoogleCalendarInvalidFeatureSetup',
    message:
      'Google Calendar feature is not setup properly! Please contact your account manager or try again later.',
  },
  googleCalendarExistingEventNotFound: {
    code: 'GCI02',
    name: 'GoogleCalendarExistingEventNotFound',
    message: 'Google Calendar event or event ID not found.',
  },
  googleCalendarReservableFeatureNotFound: {
    code: 'GCI03',
    name: 'GoogleCalendarReservableFeatureNotFound',
    message:
      'Google Calendar feature is not setup properly! Please contact your account manager or try again later.',
  },
  googleCalendarTimeNotAvailableError: {
    code: 'GCI04',
    name: 'GoogleCalendarTimeNotAvailableError',
    message:
      'This time is no longer available. Please select another timeslot and try again.',
  },
  essensysOnlinePlanError: {
    code: 'EOP01',
    name: 'EssensysOnlinePlanError',
    message: 'Online Plan error.  Please contact admin.',
  },
  deleteChannelChildrenError: {
    code: 'DC01',
    name: 'deleteChannelChildrenError',
    message: 'Children channels must be removed in order to delete a channel',
  },
  deleteChannelSubChannelError: {
    code: 'DC02',
    name: 'deleteChannelSubChannelError',
    message: 'Sub channels must be removed in order to delete a channel',
  },
  deleteChannelUsersError: {
    code: 'DC02',
    name: 'deleteChannelUsersError',
    message: 'Users must be removed in order to delete a channel',
  },
  deleteUserMaskError: {
    code: 'DU01',
    name: 'deleteUserMaskError',
    message: 'Error masking user and fetching profileId',
  },
  deleteUserMaskProfileError: {
    code: 'DU02',
    name: 'deleteUserMaskProfileError',
    message: "Error masking user's profile",
  },
  deleteUserLoginsError: {
    code: 'DU03',
    name: 'deleteUserLoginsError',
    message: "Error deleting user's logins",
  },
  deleteUserSessionsError: {
    code: 'DU04',
    name: 'deleteUserSessionsError',
    message: "Error deleting user's sessions",
  },
  deleteUserEventSubscriptionsError: {
    code: 'DU05',
    name: 'deleteUserEventSubscriptionsError',
    message: "Error deleting user's event subscriptions",
  },
  deleteUserGroupRolesError: {
    code: 'DU06',
    name: 'deleteUserGroupRolesError',
    message: "Error deleting user's group roles",
  },
  deleteUserIntegrationsError: {
    code: 'DU07',
    name: 'deleteUserIntegrationsError',
    message: "Error deleting user's integrations",
  },
  deleteUserMaskPaymentAccountsError: {
    code: 'DU08',
    name: 'deleteUserMaskPaymentAccountsError',
    message: "Error masking user's payment accounts",
  },
  deleteUserDeviceTokenError: {
    code: 'DU09',
    name: 'deleteUserDeviceTokenError',
    message: "Error deleting user's device tokens",
  },
  disableUserError: {
    code: 'DIU01',
    name: 'disableUserError',
    message: 'Error disabling user',
  },
  enableUserError: {
    code: 'EUS01',
    name: 'enableUserError',
    message: 'Error enabling user',
  },
  enableDeletedUserError: {
    code: 'EUS02',
    name: 'enableDeletedUserError',
    message: 'Error deleted users cannot be re-enabled',
  },
  essensysBookingZeroAmountError: {
    code: 'EI06',
    name: 'essensysBookingZeroAmountError',
    message: 'The client type cannot book room at 0 amount.',
  },
  essensysBookingNegativeAmountError: {
    code: 'EI07',
    name: 'essensysBookingNegativeAmountError',
    message: 'Negative amount is not allowed.',
  },
  channelModuleNotFoundError: {
    code: 'CM01',
    name: 'ChannelModuleNotFoundError',
    message: 'Channel module name you requested for is not found.',
    status: StatusCodes.NOT_FOUND,
  },
  channelModuleAlreadyExistError: {
    code: 'CM02',
    name: 'channelModuleAlreadyExistError',
    message: 'Channel module already exists for this channel',
    status: StatusCodes.CONFLICT,
  },
  featureNameNotFoundError: {
    code: 'CM03',
    name: 'FeatureNameNotFoundError',
    message: 'Feature name not found',
    status: StatusCodes.NOT_FOUND,
  },
  channelModuleTypeError: {
    code: 'CM04',
    name: 'ChannelModuleTypeError',
    message: 'Only channel with type property allowed.',
    status: StatusCodes.BAD_REQUEST,
  },
  channelModulePramsError: {
    code: 'CM05',
    name: 'ChannelModuleParamsError',
    message: 'Required parameters of Channel module to be are missing.',
    status: StatusCodes.BAD_REQUEST,
  },
  channelNotFoundError: {
    code: 'CM06',
    name: 'ChannelNotFoundError',
    message: 'Channel you requested for is not found.',
    status: StatusCodes.NOT_FOUND,
  },
  hardwareManagementNotEnabledError: {
    code: 'HM01',
    name: 'HardwareManagementNotEnabledError',
    message: 'Hardware management not enabled for channel you requested.',
    status: StatusCodes.FORBIDDEN,
  },
  serviceUnavailableError: {
    code: 'SVC01',
    name: 'ServiceUnavailableError',
    message: 'Service is unavailable',
    status: StatusCodes.SERVICE_UNAVAILABLE,
  },
  continuedAuthForbiddenError: {
    code: 'AUTH02',
    name: 'ContinuedAuthForbiddenError',
    message: 'Continued authorization is forbidden, preferring redirect.',
  },
  homeChannelForbiddenError: {
    code: 'HC01',
    name: 'HomeChannelForbiddenError',
    message: 'You are not a member of the requested channel.',
    status: StatusCodes.FORBIDDEN,
  },
  unsupportedMediaTypeError: {
    code: 'MED01',
    name: 'UnsupportedMediaTypeError',
    message: 'This media type is unsupported.',
  },
  contentOnChannelViaAudiencesSearchError: {
    code: 'AUD01',
    name: 'ContentOnChannelViaAudiencesSearchError',
    message: 'Search parameters are malformed.',
  },
  generateSignedUrlError: {
    code: 'GSU01',
    name: 'generateSignedUrlError',
    message: 'Failed to generate signed URL from S3',
    status: StatusCodes.BAD_REQUEST,
  },
  s3UploadError: {
    code: 'SUE01',
    name: 's3UploadError',
    message: 'Failed to Upload the object to S3',
    status: StatusCodes.BAD_REQUEST,
  },
  ccureInvalidFieldError: {
    code: 'IG01',
    name: 'ccureInvalidFieldError',
    message: 'Missing required parameters for CCURE.',
  },
  geneaInvalidFieldError: {
    code: 'IG02',
    name: 'geneaInvalidFieldError',
    message: 'Missing required parameters for GENEA.',
  },
  unsupportedAttachmentTypeError: {
    code: 'UAT01',
    name: 'unsupportedAttachmentTypeError',
    message: 'This attachment type is not supported',
  },
  reservablebookingsCSVExportRowLimitError: {
    code: 'RB01',
    name: 'ReservablebookingsCSVExportRowLimitError',
    message: 'The CSV file cannot exceed 10000 rows',
    status: StatusCodes.BAD_REQUEST,
  },
  braxosInvalidFieldError: {
    code: 'IG03',
    name: 'braxosInvalidFieldError',
    message: 'Missing required parameters for BRAXOS.',
  },
  openpathInvalidFieldError: {
    code: 'IG04',
    name: 'openpathInvalidFieldError',
    message: 'Missing required parameters for OPENPATH.',
  },
  brivoInvalidFieldError: {
    code: 'IG05',
    name: 'brivoInvalidFieldError',
    message: 'Missing required parameters for BRIVO.',
  },
  yardiInvalidFieldError: {
    code: 'IG06',
    name: 'yardiInvalidFieldError',
    message: 'Missing required parameters for YARDI.',
  },
  maxxessInvalidFieldError: {
    code: 'IG06',
    name: 'maxxessInvalidFieldError',
    message: 'Missing required parameters for MAXXESS.',
  },
  gallagherInvalidFieldError: {
    code: 'IG06',
    name: 'gallagherInvalidFieldError',
    message: 'Missing required parameters for GALLAGHER.',
  },
  gallagherInvalidClientCertificateError: {
    code: 'IG07',
    name: 'gallagherInvalidClientCertificateError',
    message: 'Invalid Client Certificate for GALLAGHER.',
  },
  gallagherInvalidCertificatePassphraseError: {
    code: 'IG08',
    name: 'gallagherInvalidCertificatePassphraseError',
    message: 'Invalid Certificate Passphrase for GALLAGHER.',
  },
  gallagherClientCertificateValidationError: {
    code: 'IG09',
    name: 'gallagherClientCertificateValidationError',
    message:
      'Something went wrong while validating the client certificate for GALLAGHER.',
  },
  dsxInvalidFieldError: {
    code: 'IG10',
    name: 'dsxInvalidFieldError',
    message: 'Missing required parameters for DSX.',
  },
  genetecInvalidFieldError: {
    code: 'IG10',
    name: 'genetecInvalidFieldError',
    message: 'Missing required parameters for GENETEC.',
  },
  saltoSvnInvalidFieldError: {
    code: 'IG10',
    name: 'saltoSvnInvalidFieldError',
    message: 'Missing required parameters for SaltoSvn.',
  },
  kastleInvalidFieldError: {
    code: 'IG11',
    name: 'kastleInvalidFieldError',
    message: 'Missing required parameters for Kastle.',
  },
  HIDInvalidFieldError: {
    code: 'IG10',
    name: 'HIDInvalidFieldError',
    message: 'Missing required parameters for HID.',
  },
  mailchimpInvalidFieldError: {
    code: 'IG11',
    name: 'mailchimpInvalidFieldError',
    message: 'Missing required parameters for Mailchimp.',
  },
  userNotFoundError: {
    code: 'US01',
    name: 'userNotFoundError',
    message: 'User not found.',
  },
  userStatusInvalidError: {
    code: 'US02',
    name: 'userStatusInvalidError',
    message: 'Invalid user status',
  },
  internalError: {
    code: 'IE01',
    name: 'internalError',
    message: 'Something went wrong',
    status: StatusCodes.INTERNAL_SERVER_ERROR,
  },
  invalidVisitorEndTimeError: {
    code: 'VET01',
    name: 'invalidEndTimeError',
    message: 'The time window must be set to a future date and time',
  },
  invalidVisitorScheduleRangeError: {
    code: 'VSR01',
    name: 'invalidScheduleRangeError',
    message: 'The visit start time must be before the end time',
  },
  invalidVisitorNameCharactersError: {
    code: 'VNC01',
    name: 'invalidVisitorNameCharactersError',
    message:
      "A visitor name contains a character this building's access system can't accept",
  },
  auth0OrganizationNotFoundError: {
    code: 'AU01',
    name: 'auth0OrganizationNotFoundError',
    message: 'Auth0 organization not found.',
    status: StatusCodes.NOT_FOUND,
  },
  auth0OrganizationInvitationError: {
    code: 'AU02',
    name: 'auth0OrganizationInvitationError',
    message: 'Unable to create Auth0 invitation.',
    status: StatusCodes.INTERNAL_SERVER_ERROR,
  },
  contentAlreadyPinnedToSectionError: {
    code: 'SEC01',
    name: 'ContentAlreadyPinnedToSectionError',
    message:
      'Unable to pin content to the section because it is already pinned',
  },
  contentNotPinnedToSectionError: {
    code: 'SEC02',
    name: 'ContentNotPinnedToSectionError',
    message: 'Unable to unpin content because it is not pinned to the section.',
  },
  oidcIntegrationError: {
    code: 'OIDC01',
    name: 'OIDCIntegrationError',
    message: 'Something wrong with OIDC integration',
  },
  invalidTaskStatusTransitionError: {
    code: 'TS01',
    name: 'InvalidTaskStatusTransitionError',
    message: 'Invalid task status transition',
    status: StatusCodes.BAD_REQUEST,
  },
  ...unitErrorCodeTypes,
  ...floorErrorCodeTypes,
  ...tenantErrorCodeTypes,
  ...workflowErrorCodeTypes,
  ...workOrdersErrorCodeTypes,
  ...phoneVerificationErrorCodeTypes,
} as const;

export {
  unitErrorCodeTypes,
  floorErrorCodeTypes,
  tenantErrorCodeTypes,
  workflowErrorCodeTypes,
  workOrdersErrorCodeTypes,
};
